/**
 * AgentList Component
 * Displays connected agents and their status
 * Redesigned for clarity and non-technical accessibility
 */

import React from 'react';
import { AlertTriangle, CheckCircle2, Clock, XCircle, Bot } from 'lucide-react';
import { useDashboardStore } from '../store/dashboardStore';

const STATUS_CONFIG = {
  active: {
    label: 'Active',
    description: 'Currently working',
    dot: 'bg-emerald-500',
    badge: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    icon: <CheckCircle2 className="w-3.5 h-3.5" />,
    ring: 'ring-emerald-200',
  },
  online: {
    label: 'Active',
    description: 'Currently working',
    dot: 'bg-emerald-500',
    badge: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    icon: <CheckCircle2 className="w-3.5 h-3.5" />,
    ring: 'ring-emerald-200',
  },
  idle: {
    label: 'Idle',
    description: 'Waiting for tasks',
    dot: 'bg-amber-400',
    badge: 'bg-amber-400/10 text-amber-400 border-amber-400/20',
    icon: <Clock className="w-3.5 h-3.5" />,
    ring: 'ring-amber-200',
  },
  error: {
    label: 'Error',
    description: 'Needs attention',
    dot: 'bg-red-500',
    badge: 'bg-red-500/10 text-red-400 border-red-500/20',
    icon: <XCircle className="w-3.5 h-3.5" />,
    ring: 'ring-red-200',
  },
};

const CAPABILITY_LABELS: Record<string, string> = {
  llm: 'Language AI',
  cache: 'Caching',
  search: 'Search',
  embedding: 'Embeddings',
  tools: 'Tools',
  memory: 'Memory',
  vision: 'Vision',
  audio: 'Audio',
};

const formatCapability = (cap: string) =>
  CAPABILITY_LABELS[cap.toLowerCase()] || cap.charAt(0).toUpperCase() + cap.slice(1).toLowerCase();

const formatAgentName = (id: string) =>
  id.substring(0, 8).toUpperCase();

export const AgentList: React.FC = () => {
  const agents = useDashboardStore((state) => state.agents);
  const selectedAgentId = useDashboardStore((state) => state.selectedAgentId);
  const setSelectedAgent = useDashboardStore((state) => state.setSelectedAgent);

  const activeCount = agents.filter((a) => a.status === 'active').length;
  const idleCount = agents.filter((a) => a.status === 'idle').length;
  const errorCount = agents.filter((a) => a.status === 'error').length;

  if (agents.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full bg-transparent px-6 text-center">
        <div className="w-16 h-16 bg-white/5 rounded-2xl flex items-center justify-center mb-4 border border-white/5">
          <Bot className="w-8 h-8 text-slate-600" />
        </div>
        <p className="text-white font-bold text-base">No agents connected</p>
        <p className="text-slate-500 text-sm mt-2 leading-relaxed max-w-[180px]">
          Start an AI agent to see it appear here and begin processing requests.
        </p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-transparent">
      {/* Header */}
      <div className="px-5 py-4 border-b border-white/5 bg-white/5 sticky top-0 z-10">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-sm font-bold text-white flex items-center gap-2">
              <Bot className="w-4 h-4 text-indigo-400" />
              AI Agents
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">{agents.length} connected right now</p>
          </div>
        </div>

        {/* Status Summary Pills */}
        <div className="flex items-center gap-1.5 flex-wrap">
          {activeCount > 0 && (
            <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest px-2.5 py-1 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
              {activeCount} Working
            </span>
          )}
          {idleCount > 0 && (
            <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest px-2.5 py-1 rounded-full bg-amber-500/10 text-amber-400 border border-amber-500/20">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
              {idleCount} Idle
            </span>
          )}
          {errorCount > 0 && (
            <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest px-2.5 py-1 rounded-full bg-red-500/10 text-red-400 border border-red-500/20">
              <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
              {errorCount} Error
            </span>
          )}
        </div>
      </div>

      {/* Agent Cards */}
      <div className="flex-1 overflow-y-auto px-3 py-3 space-y-2">
        {agents.map((agent) => {
          const status = STATUS_CONFIG[agent.status as keyof typeof STATUS_CONFIG] || STATUS_CONFIG.idle;
          const isSelected = selectedAgentId === agent.id;
          const rawHitRate = agent.cacheHitRate;
          const hasHitData = typeof rawHitRate === 'number' && !isNaN(rawHitRate) && agent.tasksCompleted > 0;
          const hitRatePct = hasHitData ? (rawHitRate * 100).toFixed(0) : null;

          return (
            <div
              key={agent.id}
              onClick={() => setSelectedAgent(agent.id)}
              className={`p-4 rounded-xl border transition-all duration-200 cursor-pointer select-none ${
                isSelected
                  ? 'bg-indigo-600/10 border-indigo-500/40 shadow-[0_0_20px_rgba(79,70,229,0.15)]'
                  : 'bg-white/5 border-white/5 hover:border-white/10 hover:bg-white/[0.08]'
              }`}
            >
              {/* Agent Header */}
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-start gap-2.5 min-w-0">
                  {/* Avatar */}
                  <div className={`w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-400 to-purple-500 flex items-center justify-center flex-shrink-0 text-white text-xs font-black ring-2 ${isSelected ? 'ring-indigo-300' : status.ring}`}>
                    {agent.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-bold text-white truncate leading-tight">
                      {agent.name}
                    </p>
                    <p className="text-[10px] text-slate-500 font-mono truncate mt-0.5">
                      #{formatAgentName(agent.id)}
                    </p>
                  </div>
                </div>

                {/* Status Badge */}
                <span className={`inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-tighter px-2 py-0.5 rounded-full border flex-shrink-0 ${status.badge}`}>
                  {status.icon}
                  {status.label}
                </span>
              </div>

              {/* Metrics Row */}
              <div className="flex items-center gap-2 mt-3">
                <div className="flex-1 bg-black/20 rounded-lg px-3 py-1.5 text-center border border-white/5">
                  <p className="text-sm font-black text-white">{agent.tasksCompleted}</p>
                  <p className="text-[10px] text-slate-500 uppercase font-bold tracking-tighter">Tasks done</p>
                </div>
                <div className="flex-1 bg-black/20 rounded-lg px-3 py-1.5 text-center border border-white/5">
                  <p className={`text-sm font-black ${
                    hitRatePct === null
                      ? 'text-slate-500'
                      : parseInt(hitRatePct) >= 50 ? 'text-emerald-400' : 'text-amber-400'
                  }`}>
                    {hitRatePct === null ? '—' : `${hitRatePct}%`}
                  </p>
                  <p className="text-[10px] text-slate-500 uppercase font-bold tracking-tighter">Agent Hit Rate</p>
                </div>
              </div>

              {/* Error Warning */}
              {agent.errorCount > 0 && (
                <div className="mt-2.5 flex items-center gap-1.5 text-red-600 bg-red-50 px-3 py-1.5 rounded-lg border border-red-100">
                  <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
                  <span className="text-xs font-semibold">{agent.errorCount} error{agent.errorCount !== 1 ? 's' : ''} — needs review</span>
                </div>
              )}

              {/* Capabilities */}
              {agent.capabilities.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2.5">
                  {agent.capabilities.slice(0, 3).map((cap: string) => (
                    <span
                      key={cap}
                      className="text-[10px] font-black uppercase tracking-tighter bg-indigo-500/10 text-indigo-400 border border-indigo-500/20 px-2 py-0.5 rounded-full"
                    >
                      {formatCapability(cap)}
                    </span>
                  ))}
                  {agent.capabilities.length > 3 && (
                    <span className="text-xs text-gray-400 font-medium self-center">
                      +{agent.capabilities.length - 3} more
                    </span>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Legend Footer */}
      <div className="px-4 py-3 border-t border-white/5 bg-black/10">
        <p className="text-[10px] text-slate-600 text-center font-bold uppercase tracking-widest">Tap an agent to focus its activity</p>
      </div>
    </div>
  );
};
